import {
  Button,
  Callout,
  Card,
  CardHeader,
  CodeChip,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  EmptyState,
  Field,
  Input,
  NativeSelect,
  PageHeader,
  Skeleton,
  useToast,
} from '@provenance/design';
import { SequenceResetCadence, SequenceScope, UserRole } from '@provenance/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Hash, Lock, Save } from 'lucide-react';
import { useEffect, useState } from 'react';
import { ApiError, api } from '../lib/api';
import { useDebounced } from '../lib/useDebounced';
import { useSession } from '../lib/session';
import { AmendmentLimitsCard } from '../features/configuration/AmendmentLimitsCard';

interface NumberingRow {
  documentTypeId: string;
  code: string;
  name: string;
  configured: boolean;
  certificatesIssued: number;
  prefix?: string;
  separator?: string;
  sequenceWidth?: number;
  suffix?: string;
  resetCadence?: SequenceResetCadence;
  sequenceScope?: SequenceScope;
  amendmentSuffixPattern?: string;
  currentValue?: number;
  highestIssuedValue?: number;
  periodKey?: string;
  nextNumber?: string;
  afterThat?: string;
  amendmentExample?: string;
  sharesCounterWithOtherTypes?: boolean;
}

interface FormatShape {
  prefix: string;
  separator: string;
  sequenceWidth: number;
  suffix: string;
  resetCadence: SequenceResetCadence;
  sequenceScope: SequenceScope;
  amendmentSuffixPattern: string;
}

interface PreviewResult {
  next: string;
  afterThat: string;
  amendmentExample: string;
  secondAmendmentExample: string;
  afterReset: string | null;
  warning: string | null;
}

const DEFAULT_FORMAT: FormatShape = {
  prefix: '',
  separator: '',
  sequenceWidth: 6,
  suffix: '',
  resetCadence: SequenceResetCadence.NEVER,
  sequenceScope: SequenceScope.CHAMBER,
  amendmentSuffixPattern: '-A{n}',
};

const CADENCES: Array<{ value: SequenceResetCadence; label: string; hint: string }> = [
  { value: SequenceResetCadence.NEVER, label: 'Never', hint: 'One unbroken sequence, forever' },
  { value: SequenceResetCadence.YEARLY, label: 'Yearly', hint: 'Back to 1 on 1 January' },
  { value: SequenceResetCadence.MONTHLY, label: 'Monthly', hint: 'Back to 1 each month' },
];

/**
 * Certificate numbering.
 *
 * The most consequential configuration screen in the product: a certificate number is
 * what a foreign customs authority quotes back, so a number issued twice is a real-world
 * problem that no amount of later editing fixes. Every control here previews through the
 * same pure functions the generator uses, and the seed guard refuses to move a counter
 * below anything already issued.
 */
export function Numbering() {
  const query = useQuery({
    queryKey: ['numbering'],
    queryFn: () => api.get<NumberingRow[]>('/api/console/configuration/numbering'),
  });
  const [editing, setEditing] = useState<NumberingRow | null>(null);
  const [seeding, setSeeding] = useState<NumberingRow | null>(null);
  const { user } = useSession();

  const rows = query.data ?? [];
  const unconfigured = rows.filter((r) => !r.configured);
  // Under chamber scope every document type draws from one counter, so "next number" is a
  // property of the chamber, not of the type. Printing it on eight cards reads as eight
  // separate sequences that happen to agree — which is the opposite of what is true.
  const shared = rows.filter((r) => r.configured && r.sharesCounterWithOtherTypes);

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Certificate numbers"
        lede="How your chamber builds a certificate number. A number is drawn atomically at the moment of approval and never reused — which is precisely why the format has to be right before the first one goes out."
      />

      {unconfigured.length > 0 && (
        <Callout
          tone="warn"
          icon={<AlertTriangle className="size-4" />}
          title={`${unconfigured.length} document type${unconfigured.length === 1 ? ' has' : 's have'} no number format`}
        >
          {unconfigured.map((r) => r.name).join(', ')} cannot be approved — there is nothing to
          print in the certificate-number slot.
        </Callout>
      )}

      {query.isLoading ? (
        <Skeleton className="h-64" />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<Hash className="size-5" />}
          title="No document types enabled"
          description="A platform administrator enables the document types your chamber is accredited to issue."
        />
      ) : (
        <>
          {shared.length > 0 && <SharedCounterCard rows={shared} onSeed={() => setSeeding(shared[0]!)} />}

          <div className="grid gap-3 lg:grid-cols-2">
            {rows.map((row) => (
              <Card key={row.documentTypeId}>
                <CardHeader
                  title={row.name}
                  hint={<CodeChip tone="leaf">{row.code}</CodeChip>}
                  actions={
                    <div className="flex items-center gap-1.5">
                      {row.configured &&
                        !row.sharesCounterWithOtherTypes &&
                        row.certificatesIssued === 0 && (
                          <Button variant="ghost" size="sm" onClick={() => setSeeding(row)}>
                            Set counter
                          </Button>
                        )}
                      <Button variant="secondary" size="sm" onClick={() => setEditing(row)}>
                        {row.configured ? 'Edit format' : 'Set a format'}
                      </Button>
                    </div>
                  }
                />

                {!row.configured ? (
                  <p className="[font-size:13px] text-[var(--pv-muted)]">
                    No format set. Approvals of this type will be refused.
                  </p>
                ) : (
                  <>
                    {!row.sharesCounterWithOtherTypes && (
                      <div className="rounded-[var(--pv-radius-sm)] border border-[var(--pv-line)] bg-[var(--pv-paper-sunk)] px-3 py-2.5">
                        <p className="pv-eyebrow text-[var(--pv-faint)]">Next certificate</p>
                        <p className="pv-num mt-1 [font-size:var(--pv-text-h2)] font-bold tracking-[-0.01em]">
                          {row.nextNumber}
                        </p>
                        <p className="pv-num mt-0.5 [font-size:12px] text-[var(--pv-faint)]">
                          then {row.afterThat} · amendment {row.amendmentExample}
                        </p>
                      </div>
                    )}

                    <dl className="grid grid-cols-3 gap-2 [font-size:12px]">
                      <div>
                        <dt className="text-[var(--pv-faint)]">Format</dt>
                        <dd className="pv-num truncate font-medium">{formatPattern(row)}</dd>
                      </div>
                      <div>
                        <dt className="text-[var(--pv-faint)]">Issued</dt>
                        <dd className="pv-num font-medium">{row.certificatesIssued}</dd>
                      </div>
                      <div>
                        <dt className="text-[var(--pv-faint)]">Resets</dt>
                        <dd className="font-medium">
                          {CADENCES.find((c) => c.value === row.resetCadence)?.label ?? '—'}
                        </dd>
                      </div>
                    </dl>

                    <p className="mt-2 [font-size:12px] text-[var(--pv-faint)]">
                      {row.sharesCounterWithOtherTypes
                        ? 'Numbered from the chamber-wide sequence above.'
                        : 'Counts separately from every other document type.'}
                    </p>
                  </>
                )}
              </Card>
            ))}
          </div>

          <AmendmentLimitsCard canEdit={user?.role === UserRole.CHAMBER_ADMIN} />
        </>
      )}

      {editing && (
        <FormatDialog row={editing} onClose={() => setEditing(null)} />
      )}
      {seeding && <SeedDialog row={seeding} onClose={() => setSeeding(null)} />}
    </div>
  );
}

/**
 * The format as a shape rather than an example: `BCQ/{YYYY}/######`.
 *
 * On a card that already says "numbered from the chamber-wide sequence", printing the
 * concrete next number again would be the same figure eight times over — true, but it
 * tells an administrator nothing about how *this* type is built.
 */
function formatPattern(row: NumberingRow): string {
  return `${row.prefix ?? ''}${row.separator ?? ''}${'#'.repeat(row.sequenceWidth ?? 6)}${row.suffix ?? ''}`;
}

/**
 * The chamber-wide sequence, stated once.
 *
 * Every document type under chamber scope draws the next number from here, so this is
 * where the number actually lives — and where a counter is seeded at onboarding, because
 * seeding one type under this scope seeds all of them.
 */
function SharedCounterCard({ rows, onSeed }: { rows: NumberingRow[]; onSeed: () => void }) {
  const lead = rows[0]!;
  const issued = rows.reduce((total, row) => total + row.certificatesIssued, 0);

  return (
    <Card className="!bg-[var(--pv-card-alt)]">
      <CardHeader
        title="Your chamber's sequence"
        description={`One counter shared by ${rows.length} document types, so certificate numbers are unique across your chamber and never restart per form.`}
        actions={
          issued === 0 && (
            <Button variant="secondary" size="sm" onClick={onSeed}>
              Continue an existing sequence
            </Button>
          )
        }
      />
      <div className="flex flex-wrap items-end gap-x-10 gap-y-4">
        <div>
          <p className="pv-eyebrow text-[var(--pv-faint)]">Next certificate</p>
          <p className="pv-num mt-1 [font-size:var(--pv-text-display-sm)] font-bold tracking-[-0.02em]">
            {lead.nextNumber}
          </p>
          <p className="pv-num mt-1 [font-size:12.5px] text-[var(--pv-muted)]">
            then {lead.afterThat} · amendment {lead.amendmentExample}
          </p>
        </div>
        <dl className="flex flex-wrap gap-x-8 gap-y-3 [font-size:12.5px]">
          <div>
            <dt className="text-[var(--pv-faint)]">Counter</dt>
            <dd className="pv-num font-medium">{lead.currentValue}</dd>
          </div>
          <div>
            <dt className="text-[var(--pv-faint)]">Highest issued</dt>
            <dd className="pv-num font-medium">{lead.highestIssuedValue}</dd>
          </div>
          <div>
            <dt className="text-[var(--pv-faint)]">Resets</dt>
            <dd className="font-medium">
              {CADENCES.find((c) => c.value === lead.resetCadence)?.label ?? '—'}
            </dd>
          </div>
          <div>
            <dt className="text-[var(--pv-faint)]">Issued so far</dt>
            <dd className="pv-num font-medium">{issued}</dd>
          </div>
        </dl>
      </div>
    </Card>
  );
}

function FormatDialog({ row, onClose }: { row: NumberingRow; onClose: () => void }) {
  const toast = useToast();
  const queryClient = useQueryClient();

  const [format, setFormat] = useState<FormatShape>(() => ({
    prefix: row.prefix ?? DEFAULT_FORMAT.prefix,
    separator: row.separator ?? DEFAULT_FORMAT.separator,
    sequenceWidth: row.sequenceWidth ?? DEFAULT_FORMAT.sequenceWidth,
    suffix: row.suffix ?? DEFAULT_FORMAT.suffix,
    resetCadence: row.resetCadence ?? DEFAULT_FORMAT.resetCadence,
    sequenceScope: row.sequenceScope ?? DEFAULT_FORMAT.sequenceScope,
    amendmentSuffixPattern: row.amendmentSuffixPattern ?? DEFAULT_FORMAT.amendmentSuffixPattern,
  }));

  // The preview is a round trip so that what an administrator reads is produced by the
  // generator's own functions, not by a second implementation in the browser that could
  // drift from it. Debounced so typing a prefix is not one request per keystroke.
  const debounced = useDebounced(format, 250);
  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const fromValue = row.currentValue ?? 0;

  useEffect(() => {
    let cancelled = false;
    api
      .post<PreviewResult>('/api/console/configuration/numbering/preview', {
        prefix: debounced.prefix,
        separator: debounced.separator,
        sequenceWidth: debounced.sequenceWidth,
        suffix: debounced.suffix,
        resetCadence: debounced.resetCadence,
        amendmentSuffixPattern: debounced.amendmentSuffixPattern,
        fromValue,
      })
      .then((result) => {
        if (!cancelled) setPreview(result);
      })
      .catch(() => {
        if (!cancelled) setPreview(null);
      });
    return () => {
      cancelled = true;
    };
  }, [debounced, fromValue]);

  const save = useMutation({
    mutationFn: () =>
      api.post(`/api/console/configuration/numbering/${row.documentTypeId}`, format),
    onSuccess: () => {
      toast.success('Number format saved', `The next certificate will be ${preview?.next ?? 'numbered accordingly'}.`);
      void queryClient.invalidateQueries({ queryKey: ['numbering'] });
      void queryClient.invalidateQueries({ queryKey: ['stamping-overview'] });
      onClose();
    },
    onError: (error) =>
      toast.error('Could not save the format', error instanceof ApiError ? error.message : ''),
  });

  const locked = (row.certificatesIssued ?? 0) > 0;
  const patch = (p: Partial<FormatShape>) => setFormat((prev) => ({ ...prev, ...p }));

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[42rem]">
        <DialogHeader
          title={`Number format — ${row.name}`}
          description="Every part is optional except the sequence itself. The preview below is produced by the generator, not by a copy of it."
        />
        <DialogBody>
          <div className="flex flex-col gap-4">
            <div className="rounded-[var(--pv-radius)] border border-[var(--pv-line)] bg-[var(--pv-paper-sunk)] px-4 py-3.5">
              <p className="pv-eyebrow text-[var(--pv-faint)]">Next certificate number</p>
              <p className="pv-num mt-1 [font-size:var(--pv-text-h1)] font-bold tracking-[-0.02em]">
                {preview?.next ?? '…'}
              </p>
              {preview && (
                <p className="pv-num mt-1 [font-size:12.5px] text-[var(--pv-muted)]">
                  then {preview.afterThat} · first amendment {preview.amendmentExample} · second{' '}
                  {preview.secondAmendmentExample}
                  {preview.afterReset && ` · after the next reset ${preview.afterReset}`}
                </p>
              )}
            </div>

            {preview?.warning && (
              <Callout tone="warn" icon={<AlertTriangle className="size-4" />} title="This format repeats itself">
                {preview.warning}
              </Callout>
            )}

            {locked && (
              <Callout tone="info" icon={<Lock className="size-4" />} title="Certificates have already been issued">
                {row.certificatesIssued} certificate{row.certificatesIssued === 1 ? '' : 's'} already
                carry this format. Changing it affects only certificates issued from now on — nothing
                already in circulation is altered.
              </Callout>
            )}

            <div className="grid gap-3 sm:grid-cols-3">
              <Field label="Prefix" help="Supports {YYYY}, {YY} and {MM}.">
                <Input
                  value={format.prefix}
                  onChange={(e) => patch({ prefix: e.target.value })}
                  placeholder="BCQ/{YYYY}/"
                />
              </Field>
              <Field label="Separator" help="Between prefix and number.">
                <Input value={format.separator} onChange={(e) => patch({ separator: e.target.value })} />
              </Field>
              <Field label="Digits" help="Zero-padded to this width." required>
                <Input
                  type="number"
                  min={1}
                  max={12}
                  value={format.sequenceWidth}
                  onChange={(e) => patch({ sequenceWidth: Number(e.target.value) })}
                />
              </Field>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Suffix">
                <Input value={format.suffix} onChange={(e) => patch({ suffix: e.target.value })} />
              </Field>
              <Field label="Amendment suffix" help="{n} is the amendment number." required>
                <Input
                  value={format.amendmentSuffixPattern}
                  onChange={(e) => patch({ amendmentSuffixPattern: e.target.value })}
                  placeholder="-A{n}"
                />
              </Field>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Reset cadence" help="When the sequence returns to 1.">
                <NativeSelect
                  value={format.resetCadence}
                  onChange={(e) => patch({ resetCadence: e.target.value as SequenceResetCadence })}
                >
                  {CADENCES.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label} — {c.hint}
                    </option>
                  ))}
                </NativeSelect>
              </Field>
              <Field
                label="Counter shared with"
                help="Certificate numbers must be unique across your chamber."
              >
                <NativeSelect
                  value={format.sequenceScope}
                  onChange={(e) => patch({ sequenceScope: e.target.value as SequenceScope })}
                  disabled={locked}
                >
                  <option value={SequenceScope.CHAMBER}>
                    Every document type — one chamber-wide sequence
                  </option>
                  <option value={SequenceScope.DOCUMENT_TYPE}>
                    This type only — its own sequence
                  </option>
                </NativeSelect>
              </Field>
            </div>

            {format.sequenceScope === SequenceScope.DOCUMENT_TYPE && (
              <Callout tone="warn" icon={<AlertTriangle className="size-4" />} title="Separate counters can collide">
                With its own counter, this type will produce the same numbers as any other type
                that also counts separately — unless their prefixes differ. Give this type a
                distinct prefix.
              </Callout>
            )}
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            leadingIcon={<Save className="size-3.5" />}
            loading={save.isPending}
            onClick={() => save.mutate()}
          >
            Save format
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Seeding exists for one situation: a chamber that has been issuing on paper and is
 * continuing its existing numbering here. It is deliberately only offered before the
 * first certificate is issued, and the server refuses any value below what has been
 * issued already.
 */
function SeedDialog({ row, onClose }: { row: NumberingRow; onClose: () => void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [value, setValue] = useState(String(row.currentValue ?? 0));

  const seed = useMutation({
    mutationFn: () =>
      api.post<{ currentValue: number; nextNumber: string }>(
        `/api/console/configuration/numbering/${row.documentTypeId}/seed`,
        { value: Number(value) },
      ),
    onSuccess: (result) => {
      toast.success('Counter set', `The next certificate will be ${result.nextNumber}.`);
      void queryClient.invalidateQueries({ queryKey: ['numbering'] });
      onClose();
    },
    onError: (error) =>
      toast.error('Could not set the counter', error instanceof ApiError ? error.message : ''),
  });

  const parsed = Number(value);
  const valid = Number.isInteger(parsed) && parsed >= 0;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader
          title={`Continue an existing sequence — ${row.name}`}
          description="If your chamber has been issuing on paper, set the counter to the last number you issued. The next certificate continues from there."
        />
        <DialogBody>
          <div className="flex flex-col gap-3.5">
            <Field
              label="Last number issued"
              required
              help="Just the numeric part — not the prefix. The counter cannot be moved below anything already issued here."
            >
              <Input
                type="number"
                min={0}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                className="pv-num"
              />
            </Field>
            {valid && (
              <div className="rounded-[var(--pv-radius-sm)] border border-[var(--pv-line)] bg-[var(--pv-paper-sunk)] px-3 py-2.5">
                <p className="pv-eyebrow text-[var(--pv-faint)]">The next certificate will be</p>
                <p className="pv-num mt-1 [font-size:var(--pv-text-h3)] font-bold">
                  {row.prefix ?? ''}
                  {row.separator ?? ''}
                  {String(parsed + 1).padStart(row.sequenceWidth ?? 6, '0')}
                  {row.suffix ?? ''}
                </p>
                <p className="mt-1 [font-size:12px] text-[var(--pv-faint)]">
                  Any year tokens in the prefix resolve at issue time.
                </p>
              </div>
            )}
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" disabled={!valid} loading={seed.isPending} onClick={() => seed.mutate()}>
            Set counter
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
